/**
 * Detects whether the current device is mobile based on viewport width
 * and pointer capability, and provides reactive state updates on resize.
 */
export class MobileDetector {
  private _isMobile: boolean;
  private listeners: Array<(isMobile: boolean) => void>;
  private onResize: () => void;
  static readonly BREAKPOINT = 768;

  constructor() {
    this.listeners = [];
    this._isMobile = this.detect();

    this.onResize = () => {
      const newState = this.detect();
      if (newState !== this._isMobile) {
        this._isMobile = newState;
        this.listeners.forEach((listener) => listener(this._isMobile));
      }
    };

    window.addEventListener('resize', this.onResize);
  }

  /**
   * Returns true if the device is considered mobile:
   * - viewport width <= 768px, OR
   * - device has only a coarse pointer (touch-only, no fine pointer)
   */
  private detect(): boolean {
    const isNarrowViewport = window.innerWidth <= MobileDetector.BREAKPOINT;
    const isCoarseOnly =
      window.matchMedia('(pointer: coarse)').matches &&
      !window.matchMedia('(pointer: fine)').matches;

    return isNarrowViewport || isCoarseOnly;
  }

  /** Read-only accessor for current mobile detection state. */
  get isMobile(): boolean {
    return this._isMobile;
  }

  /** Register a listener to be notified when mobile state changes. */
  onChange(listener: (isMobile: boolean) => void): void {
    this.listeners.push(listener);
  }

  /** Remove resize listener and clean up resources. */
  dispose(): void {
    window.removeEventListener('resize', this.onResize);
    this.listeners = [];
  }
}
